import { isNil } from 'lodash'
import { getWebViewVersion } from './is-in-web-view'

/**
 * Find the corresponding types on the mobile app in hager-energy-mobile-apps\src\webview-app-utils\message-handler.ts
 *
 * @todo remove note once version has been released
 * Type "csv-utf8" is not supported in WEBVIEW_VERSION 1+
 * Type "nav-to-login" is supported in WEBVIEW_VERSION 1+
 * Type "xlsx" is supported in WEBVIEW_VERSION 2+
 */
type WebAppMessage =
  | {
      type: 'pdf' | 'csv' | 'csv-utf8' | 'nav-to-login'
      data: string
    }
  | { type: 'xlsx'; data: { fileName: string; data: (string | number)[][] } }
  | { type: 'init-ios-login' }

type RNWebView = {
  ReactNativeWebView: {
    postMessage: (data: string) => void
  }
}

/**
 * If we are inside React Native's webview, a ReactNativeWebView object is injected into window,
 * which contains a "postMessage" function that sends messages to ReactNative.
 * It returns true if that function is found, false otherwise.
 */
const isReactNativeWebviewMessengerPresent = (): boolean => !!(window as unknown as RNWebView)?.ReactNativeWebView?.postMessage

/**
 *
 * Function which takes the chart data and the headers, converts them into a csv format and
 * a utf-8 string and sends it to ReactNative which saves the data into a csv file
 */
export const sendReactNativeCsvData = (
  headers: {
    label: string
    key: string
  }[],
  savedCsvData: Record<string, string | number | boolean>[],
  separator: ',' | ';'
): void => {
  if (!isReactNativeWebviewMessengerPresent()) return
  const rnWindow = (window as unknown as RNWebView).ReactNativeWebView

  const parsedCsv = csvParser(headers, savedCsvData, separator)

  const webviewVersion = getWebViewVersion()

  /**q
   * Version 0 does not support utf8 data.
   * @todo remove check once both apps have been successfully released new versions.
   */
  if (webviewVersion >= 1) {
    const message: WebAppMessage = {
      type: 'csv-utf8',
      data: parsedCsv,
    }
    rnWindow.postMessage(JSON.stringify(message))
  } else {
    /* Version 0 does not support utf8 data. */
    const message: WebAppMessage = {
      type: 'csv',
      data: window.btoa(parsedCsv),
    }
    rnWindow.postMessage(JSON.stringify(message))
  }
}

/**
 * Function which takes a matrix of data and sends it to ReactNative to be saved as a xslx file
 */
export const sendReactNativeExcelData = (data: (string | number)[][], fileName: string): void => {
  if (!isReactNativeWebviewMessengerPresent()) return
  const rnWindow = (window as unknown as RNWebView).ReactNativeWebView

  const webviewVersion = getWebViewVersion()
  /**
   * Feature is only supported in version 2+.
   * @todo remove check once both apps have been successfully released new versions.
   */
  if (webviewVersion < 2) return

  const message: WebAppMessage = {
    type: 'xlsx',
    data: { data, fileName },
  }
  rnWindow.postMessage(JSON.stringify(message))
}

/**
 * Formats data into an appropriate csv format
 */
export function csvParser(
  headers: {
    label: string
    key: string
  }[],
  data: Record<string, string | number | boolean>[],
  separator: ',' | ';'
): string {
  const parsedHeaders = headers.map(headerItem => parseCsvValue(headerItem.label)).join(separator) + '\n'
  const parsedContent = data.map(csvItem => headers.map(header => parseCsvValue(csvItem[header.key])).join(separator)).join('\n')

  return parsedHeaders + parsedContent
}

/**
 * The parseCsvValue function adapts the values to the csv standard regarding the quotes
 */
function parseCsvValue(value: string | number | boolean | null | undefined): string {
  if (isNil(value)) return ''

  return `"${value.toString().replace(/"/g, '""')}"`
}
